import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DataTable, type ColumnDef, type ActionDef } from "@/components/data-table/DataTable";
import { Dialog } from "@/components/ui/Dialog";
import { FieldButton, FieldInput, FieldTextArea, FieldJSON, FieldSlider } from "@/components/ui/Field";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { usePlatformAPI } from "@/api/PlatformProvider";
import { type APIError } from "@/api/APIError";
import { useGolemErrors } from "@/lib/golem/useGolemErrors";
import { WarningBox } from "@/components/ui/WarningBox";
import { isDev } from "@/config/env";


type PartnerApp = {
    id: string;
    name: string;
    description?: string;
    notificationEmail: string;
    scope?: Record<string, unknown>;
    requireSigning: boolean;
    rollAfterDays?: number;
    rolloverDays?: number;
};

type Credential = {
    id: string;
    requireSigning: boolean;
    createdAt: string;
    expiresAt?: string;
    revokedAt?: string;
    retiredAt?: string;
    lastUsedAt?: string;
};

type Claim = {
    id: string;
    attemptCount: number;
    createdAt: string;
    expiresAt: string;
    usedAt?: string;
    revokedAt?: string;
};

type CredentialRow = Credential & { status: "Active" | "Retiring" | "Retired" | "Expired" | "Revoked" };

function credentialStatus(c: Credential, rolloverDays?: number): CredentialRow["status"] {
    if (c.revokedAt) return "Revoked";
    if (c.retiredAt) return "Retired";
    if (c.expiresAt) {
        const expires = new Date(c.expiresAt);
        if (expires <= new Date()) return "Expired";
        if (rolloverDays) {
            const rolloverStart = new Date(expires.getTime() - rolloverDays * 86400_000);
            if (new Date() >= rolloverStart) return "Retiring";
        }
        return "Active";
    }
    return "Active";
}

function fmtDate(v: unknown) {
    if (!v) return "—";
    return new Date(v as string).toLocaleString();
}

const MAX_CLAIM_ATTEMPTS = 5;

function claimStatus(c: Claim): "Pending" | "Claimed" | "Revoked" | "Locked" | "Expired" {
    if (c.usedAt) return "Claimed";
    if (c.revokedAt) return "Revoked";
    if (c.attemptCount >= MAX_CLAIM_ATTEMPTS) return "Locked";
    if (new Date(c.expiresAt) <= new Date()) return "Expired";
    return "Pending";
}

const claimColumns: ColumnDef<Claim>[] = [
    { key: "createdAt", label: "Sent", width: "1fr", required: true, render: fmtDate },
    { key: "expiresAt", label: "Expires", width: "1fr", render: fmtDate },
    { key: "attemptCount", label: "Attempts", width: "100px" },
    {
        key: "usedAt",
        label: "Status",
        width: "120px",
        render: (_, row) => {
            const status = claimStatus(row);
            if (status === "Pending") return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-light-orange text-darker-orange">Pending</span>;
            if (status === "Claimed") return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-desaturated-green text-dark-green">Claimed</span>;
            if (status === "Revoked") return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-desaturated-red text-dark-red">Revoked</span>;
            if (status === "Locked") return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-desaturated-red text-dark-red">Locked</span>;
            return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-desaturated-grey text-grey">Expired</span>;
        },
    },
];


function AppCredentialsPage() {
    const { id, appId } = useParams<{ id: string; appId: string }>();
    const navigate = useNavigate();
    const api = usePlatformAPI();

    const [partnerName, setPartnerName] = useState<string | null>(null);
    const [app, setApp] = useState<PartnerApp | null>(null);
    const [tableKey, setTableKey] = useState(0);
    const [showResendModal, setShowResendModal] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [resendError, setResendError] = useState<APIError | null>(null);
    const [showPortalCodeModal, setShowPortalCodeModal] = useState(false);
    const [portalCodeLoading, setPortalCodeLoading] = useState(false);
    const [portalCodeError, setPortalCodeError] = useState<APIError | null>(null);
    const [newPortalCode, setNewPortalCode] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editName, setEditName] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState<APIError | null>(null);
    const editGolemErrors = useGolemErrors();
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [editEmail, setEditEmail] = useState("");
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailError, setEmailError] = useState<APIError | null>(null);
    const emailGolemErrors = useGolemErrors();
    const [showScopeModal, setShowScopeModal] = useState(false);
    const [editScope, setEditScope] = useState("{}");
    const [scopeLoading, setScopeLoading] = useState(false);
    const [scopeError, setScopeError] = useState<APIError | null>(null);
    const scopeGolemErrors = useGolemErrors();
    const [showCredSettingsModal, setShowCredSettingsModal] = useState(false);
    const [editRequireSigning, setEditRequireSigning] = useState(false);
    const [editRollAfterDays, setEditRollAfterDays] = useState("0");
    const [editRolloverDays, setEditRolloverDays] = useState("7");
    const [credSettingsLoading, setCredSettingsLoading] = useState(false);
    const [credSettingsError, setCredSettingsError] = useState<APIError | null>(null);
    const [showDevResetModal, setShowDevResetModal] = useState(false);
    const [devResetLoading, setDevResetLoading] = useState(false);
    const [devResetError, setDevResetError] = useState<APIError | null>(null);
    const [showDevRolloverModal, setShowDevRolloverModal] = useState(false);
    const [devRolloverLoading, setDevRolloverLoading] = useState(false);
    const [devRolloverError, setDevRolloverError] = useState<APIError | null>(null);

    const DESCRIPTION_PREVIEW = 300;

    useEffect(() => {
        api.get<{ name: string }>(`api/partners/${id}`).then(([data, err]) => {
            if (!err) setPartnerName(data.name);
        });
        api.get<PartnerApp>(`api/partners/${id}/apps/${appId}`).then(([data, err]) => {
            if (!err) setApp(data);
        });
    }, [api, id, appId]);

    const handleResend = async () => {
        setResendLoading(true);
        setResendError(null);
        const [, err] = await api.post(`api/partners/${id}/apps/${appId}/resend-claim`);
        setResendLoading(false);
        if (err) { setResendError(err); return; }
        setShowResendModal(false);
        setTableKey(k => k + 1);
    };

    const handleRegeneratePortalCode = async () => {
        setPortalCodeLoading(true);
        setPortalCodeError(null);
        const [data, err] = await api.post<{ portalCode: string }>(`api/partners/${id}/apps/${appId}/regenerate-portal-code`);
        setPortalCodeLoading(false);
        if (err) { setPortalCodeError(err); return; }
        setNewPortalCode(data.portalCode);
    };

    const openEditModal = () => {
        setEditName(app?.name ?? "");
        setEditDescription(app?.description ?? "");
        setEditError(null);
        editGolemErrors.clearErrors();
        setShowEditModal(true);
    };

    const handleEdit = async () => {
        setEditLoading(true);
        setEditError(null);
        editGolemErrors.clearErrors();
        const [data, err] = await api.put<PartnerApp>(`api/partners/${id}/apps/${appId}`, {
            name: editName,
            description: editDescription || null,
        });
        setEditLoading(false);
        if (err) { editGolemErrors.setFromError(err); setEditError(err); return; }
        setApp(data);
        setShowEditModal(false);
    };

    const openEmailModal = () => {
        setEditEmail(app?.notificationEmail ?? "");
        setEmailError(null);
        emailGolemErrors.clearErrors();
        setShowEmailModal(true);
    };

    const handleUpdateEmail = async () => {
        setEmailLoading(true);
        setEmailError(null);
        emailGolemErrors.clearErrors();
        const [data, err] = await api.put<PartnerApp>(`api/partners/${id}/apps/${appId}/notification-email`, {
            notificationEmail: editEmail,
        });
        setEmailLoading(false);
        if (err) { emailGolemErrors.setFromError(err); setEmailError(err); return; }
        setApp(data);
        setShowEmailModal(false);
    };

    const openScopeModal = () => {
        setEditScope(JSON.stringify(app?.scope ?? {}, null, 2));
        setScopeError(null);
        scopeGolemErrors.clearErrors();
        setShowScopeModal(true);
    };

    const handleEditScope = async () => {
        let parsed: Record<string, unknown>;
        try {
            parsed = JSON.parse(editScope);
        } catch {
            scopeGolemErrors.setFromObject({ scope: ["must be valid JSON"] });
            return;
        }
        setScopeLoading(true);
        setScopeError(null);
        const [data, err] = await api.put<PartnerApp>(`api/partners/${id}/apps/${appId}/scope`, {
            scope: parsed,
        });
        setScopeLoading(false);
        if (err) { scopeGolemErrors.setFromError(err); setScopeError(err); return; }
        setApp(data);
        setShowScopeModal(false);
    };

    const openCredSettingsModal = () => {
        setEditRequireSigning(app?.requireSigning ?? false);
        setEditRollAfterDays(app?.rollAfterDays?.toString() ?? "0");
        setEditRolloverDays(app?.rolloverDays?.toString() ?? "7");
        setCredSettingsError(null);
        setShowCredSettingsModal(true);
    };

    const handleEditCredSettings = async () => {
        setCredSettingsLoading(true);
        setCredSettingsError(null);
        const [data, err] = await api.put<PartnerApp>(`api/partners/${id}/apps/${appId}/credential-settings`, {
            requireSigning: editRequireSigning,
            rollAfterDays: parseInt(editRollAfterDays, 10) || null,
            rolloverDays: parseInt(editRolloverDays, 10) || null,
        });
        setCredSettingsLoading(false);
        if (err) { setCredSettingsError(err); return; }
        setApp(prev => prev ? { ...prev, requireSigning: data.requireSigning, rollAfterDays: data.rollAfterDays, rolloverDays: data.rolloverDays } : prev);
        setShowCredSettingsModal(false);
    };

    const handleDevRollover = async () => {
        setDevRolloverLoading(true);
        setDevRolloverError(null);
        const [, err] = await api.post(`api/partners/${id}/apps/${appId}/dev/create-rollover-credential`);
        setDevRolloverLoading(false);
        if (err) { setDevRolloverError(err); return; }
        setShowDevRolloverModal(false);
        setTableKey(k => k + 1);
    };

    const handleDevReset = async () => {
        setDevResetLoading(true);
        setDevResetError(null);
        const [, err] = await api.delete(`api/partners/${id}/apps/${appId}/dev/reset`);
        setDevResetLoading(false);
        if (err) { setDevResetError(err); return; }
        setShowDevResetModal(false);
        setTableKey(k => k + 1);
    };

    const getClaims = useCallback(async (params: { page: number; limit: number }) => {
        const [data, err] = await api.get<{ items: Claim[]; total: number }>(
            `api/partners/${id}/apps/${appId}/claims`,
            { page: params.page, limit: params.limit },
        );
        if (err) throw err;
        return { items: data.items, total: data.total };
    }, [api, id, appId]);

    const getCredentials = useCallback(async (params: { page: number; limit: number }) => {
        const [data, err] = await api.get<{ items: Credential[]; total: number }>(
            `api/partners/${id}/apps/${appId}/credentials`,
            { page: params.page, limit: params.limit },
        );
        if (err) throw err;
        const rows: CredentialRow[] = data.items.map(c => ({ ...c, status: credentialStatus(c) }));
        return { items: rows, total: data.total };
    }, [api, id, appId]);

    const credColumns = useMemo((): ColumnDef<CredentialRow>[] => [
        {
            key: "status",
            label: "Status",
            width: "120px",
            required: true,
            render: (_, row) => {
                const status = credentialStatus(row, app?.rolloverDays);
                if (status === "Active") return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-desaturated-green text-dark-green">Active</span>;
                if (status === "Retiring") return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-light-orange text-darker-orange">Retiring</span>;
                if (status === "Retired") return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-desaturated-grey text-grey">Retired</span>;
                if (status === "Expired") return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-desaturated-grey text-grey">Expired</span>;
                return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-desaturated-red text-dark-red">Revoked</span>;
            },
        },
        { key: "createdAt", label: "Issued", width: "1fr", render: fmtDate },
        { key: "expiresAt", label: "Expires", width: "1fr", render: fmtDate },
        { key: "lastUsedAt", label: "Last Used", width: "1fr", render: fmtDate },
        {
            key: "requireSigning",
            label: "Signing",
            width: "120px",
            render: (v) => v
                ? <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-desaturated-green text-dark-green">Required</span>
                : <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-desaturated-grey text-grey">Off</span>,
        },
    ], [app?.rolloverDays]);

    const credActions: ActionDef<CredentialRow>[] = useMemo(() => [
        {
            label: "Revoke",
            hidden: (row) => row.status !== "Active" && row.status !== "Retiring" && row.status !== "Retired",
            onClick: async (row) => {
                const [, err] = await api.post(`api/partners/${id}/apps/${appId}/credentials/${row.id}/revoke`);
                if (err) throw err;
            },
            confirm: {
                title: "Revoke credential?",
                description: "This will immediately invalidate the API key. The partner will lose access and need new credentials issued.",
                confirmLabel: "Revoke",
                variant: "danger",
            },
        },
    ], [api, id, appId]);

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
                <div className="flex flex-row items-center gap-4">
                    <button onClick={() => navigate(`/partners/${id}`)} className="text-grey hover:text-dark-dark-grey flex items-center gap-1">
                        <ArrowBackIcon fontSize="small" />
                    </button>
                    <h1 className="flex items-center gap-2 text-xl">
                        <span className="text-grey font-medium">{partnerName ?? "Partner"}</span>
                        <span className="text-light-grey">/</span>
                        <span className="font-semibold">{app?.name ?? "App"}</span>
                    </h1>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <div className="w-36">
                        <FieldButton text="Edit Details" variant="secondary" onClick={openEditModal} />
                    </div>
                    <div className="w-44">
                        <FieldButton text="Notification Email" variant="secondary" onClick={openEmailModal} />
                    </div>
                    <div className="w-32">
                        <FieldButton text="Edit Scope" variant="secondary" onClick={openScopeModal} />
                    </div>
                    <div className="w-40">
                        <FieldButton text="Cred. Settings" variant="secondary" onClick={openCredSettingsModal} />
                    </div>
                    <div className="w-44">
                        <FieldButton text="Portal Code" variant="secondary" onClick={() => { setPortalCodeError(null); setNewPortalCode(null); setShowPortalCodeModal(true); }} />
                    </div>
                    <div className="w-40">
                        <FieldButton text="Send New Claim" variant="secondary" onClick={() => { setResendError(null); setShowResendModal(true); }} />
                    </div>
                    {isDev && (
                        <div className="w-32">
                            <FieldButton text="Dev Reset" variant="danger-outline" onClick={() => { setDevResetError(null); setShowDevResetModal(true); }} />
                        </div>
                    )}
                    {isDev && app?.rollAfterDays && (
                        <div className="w-44">
                            <FieldButton text="Create Rollover" variant="danger-outline" onClick={() => { setDevRolloverError(null); setShowDevRolloverModal(true); }} />
                        </div>
                    )}
                </div>
            </div>

            {app?.description && (
                <div className="bg-white rounded-md outline outline-2 outline-light-light-grey p-6 flex flex-col gap-3">
                    <h3 className="text-sm font-semibold text-dark-dark-grey">Description</h3>
                    <pre className="text-sm text-grey whitespace-pre-wrap font-sans">
                        {showFullDescription || app.description.length <= DESCRIPTION_PREVIEW
                            ? app.description
                            : app.description.slice(0, DESCRIPTION_PREVIEW) + "…"}
                    </pre>
                    {app.description.length > DESCRIPTION_PREVIEW && (
                        <button
                            onClick={() => setShowFullDescription(v => !v)}
                            className="text-xs text-light-grey hover:text-grey self-start"
                        >
                            {showFullDescription ? "Show less" : "Show more"}
                        </button>
                    )}
                </div>
            )}

            <div className="flex flex-col gap-2">
                <div>
                    <h2 className="text-sm font-semibold text-dark-dark-grey">Credentials</h2>
                    <p className="text-xs text-grey mt-0.5">Issued API keys for this app</p>
                </div>
                <DataTable<CredentialRow>
                    key={`creds-${tableKey}`}
                    id={`partner-credentials-${appId}`}
                    columns={credColumns}
                    actions={credActions}
                    getItems={getCredentials}
                    limit={5}
                    emptyState="No credentials issued yet"
                    padToLimit
                    hideSearch
                    hideColumns
                />
            </div>

            <div className="flex flex-col gap-2">
                <div>
                    <h2 className="text-sm font-semibold text-dark-dark-grey">Claim History</h2>
                    <p className="text-xs text-grey mt-0.5">Claim emails sent to this app&apos;s notification address</p>
                </div>
                <DataTable<Claim>
                    key={`claims-${tableKey}`}
                    id={`partner-claims-${appId}`}
                    columns={claimColumns}
                    getItems={getClaims}
                    limit={5}
                    emptyState="No claims sent"
                    padToLimit
                    hideSearch
                    hideColumns
                />
            </div>

            {showResendModal && (
                <Dialog
                    title="Send new claim email"
                    confirmLabel="Send new claim"
                    variant="warning"
                    error={resendError}
                    loading={resendLoading}
                    onConfirm={handleResend}
                    onClose={() => { if (!resendLoading) setShowResendModal(false); }}
                >
                    <div className="flex items-start gap-3 rounded-lg bg-light-orange/40 border border-dark-orange/20 px-4 py-3">
                        <WarningAmberIcon fontSize="small" className="text-dark-orange mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-darker-orange leading-relaxed">
                            Any pending claim links previously sent to this app will be <strong>permanently invalidated</strong> and can no longer be used.
                        </p>
                    </div>
                    <ul className="flex flex-col gap-2 text-sm text-grey">
                        <li className="flex items-start gap-2">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-light-grey flex-shrink-0" />
                            A new claim email will be sent to <span className="font-medium text-dark-dark-grey">{app?.notificationEmail}</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-light-grey flex-shrink-0" />
                            The partner must use the new link to claim their API credentials
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-light-grey flex-shrink-0" />
                            Previously issued credentials are not affected
                        </li>
                    </ul>
                </Dialog>
            )}

            {showEditModal && (
                <Dialog
                    title="Edit app details"
                    confirmLabel="Update details"
                    error={editError}
                    loading={editLoading}
                    onConfirm={handleEdit}
                    onClose={() => { if (!editLoading) setShowEditModal(false); }}
                >
                    <FieldInput label="Name" value={editName} onChange={setEditName} errors={editGolemErrors} field="name" placeholder="App name" required />
                    <FieldTextArea label="Description" value={editDescription} onChange={setEditDescription} errors={editGolemErrors} field="description" placeholder="Optional description" rows={3} maxRows={12} />
                </Dialog>
            )}

            {showEmailModal && (
                <Dialog
                    title="Notification email"
                    confirmLabel="Update email"
                    variant="warning"
                    error={emailError}
                    loading={emailLoading}
                    onConfirm={handleUpdateEmail}
                    onClose={() => { if (!emailLoading) setShowEmailModal(false); }}
                >
                    <WarningBox>
                        Claim emails and credential notifications are sent to this address. Changing it redirects future credential delivery to the new address. This does not send a new claim email.
                    </WarningBox>
                    <FieldInput label="Email address" value={editEmail} onChange={setEditEmail} errors={emailGolemErrors} field="notificationEmail" placeholder="partner@example.com" required />
                </Dialog>
            )}

            {showScopeModal && (
                <Dialog
                    title="Edit API scope"
                    confirmLabel="Update scope"
                    variant="warning"
                    error={scopeError}
                    loading={scopeLoading}
                    onConfirm={handleEditScope}
                    onClose={() => { if (!scopeLoading) setShowScopeModal(false); }}
                >
                    <div className="flex items-start gap-3 rounded-lg bg-light-orange/40 border border-dark-orange/20 px-4 py-3">
                        <WarningAmberIcon fontSize="small" className="text-dark-orange mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-darker-orange leading-relaxed">
                            Scope changes take effect <strong>immediately</strong> for all subsequent API requests from this app.
                        </p>
                    </div>
                    <ul className="flex flex-col gap-2 text-sm text-grey">
                        <li className="flex items-start gap-2">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-light-grey flex-shrink-0" />
                            Narrowing the scope may cause the partner&apos;s integrations to break if they rely on permissions being removed
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-light-grey flex-shrink-0" />
                            Existing credentials are not revoked, but will be subject to the new scope on their next request
                        </li>
                    </ul>
                    <FieldJSON label="Scope" value={editScope} onChange={setEditScope} errors={scopeGolemErrors} field="scope" minHeight="160px" showPaths />
                </Dialog>
            )}

            {showCredSettingsModal && (
                <Dialog
                    title="Credential settings"
                    confirmLabel="Update settings"
                    variant="warning"
                    error={credSettingsError}
                    loading={credSettingsLoading}
                    onConfirm={handleEditCredSettings}
                    onClose={() => { if (!credSettingsLoading) setShowCredSettingsModal(false); }}
                >
                    <WarningBox>
                        Changes apply to newly issued credentials only. Existing credentials are unaffected until the next rollover or a new claim is sent.
                    </WarningBox>

                    <div className="flex flex-col gap-4">
                        <label className={`flex items-center gap-4 cursor-pointer rounded-lg border p-3.5 transition-all select-none ${
                            editRequireSigning
                                ? "border-blue/50 bg-blue/[0.04] ring-1 ring-blue/20"
                                : "border-light-grey hover:border-grey"
                        }`}>
                            <input type="checkbox" checked={editRequireSigning} onChange={e => setEditRequireSigning(e.target.checked)} className="sr-only" />
                            <div className="flex-1">
                                <p className={`text-sm font-medium ${editRequireSigning ? "text-blue" : "text-dark-dark-grey"}`}>
                                    Require request signing
                                </p>
                                <p className="text-xs text-grey leading-relaxed mt-0.5">
                                    API requests must include an Ed25519 signature verifying they originate from the partner.
                                </p>
                            </div>
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                editRequireSigning ? "bg-blue border-blue" : "border-light-grey"
                            }`}>
                                {editRequireSigning && (
                                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
                                    </svg>
                                )}
                            </div>
                        </label>

                        <FieldSlider
                            label="Roll After"
                            value={parseInt(editRollAfterDays)}
                            onChange={v => {
                                const clamped = v > 0 && v < 30 ? 30 : v;
                                setEditRollAfterDays(clamped.toString());
                                if (clamped > 0 && parseInt(editRolloverDays) === 0) setEditRolloverDays("7");
                            }}
                            min={0} max={365}
                            formatValue={v => v === 0 ? "Disabled" : `${v} days`}
                            minLabel="Disabled" maxLabel="365d"
                        />
                        <FieldSlider
                            label="Rollover Window"
                            value={Math.max(parseInt(editRolloverDays) || 7, 7)}
                            onChange={v => setEditRolloverDays(v.toString())}
                            min={7} max={60}
                            disabled={parseInt(editRollAfterDays) === 0}
                            formatValue={v => parseInt(editRollAfterDays) === 0 ? "Disabled" : `${v} days`}
                            minLabel="7d" maxLabel="60d"
                        />
                    </div>
                </Dialog>
            )}

            {showPortalCodeModal && (
                <Dialog
                    title="Generate new portal code"
                    confirmLabel={newPortalCode ? "Done" : "Generate"}
                    cancelLabel={newPortalCode ? undefined : "Cancel"}
                    variant={newPortalCode ? "default" : "warning"}
                    error={portalCodeError}
                    loading={portalCodeLoading}
                    onConfirm={newPortalCode
                        ? () => setShowPortalCodeModal(false)
                        : handleRegeneratePortalCode}
                    onClose={() => { if (!portalCodeLoading) { setShowPortalCodeModal(false); setNewPortalCode(null); } }}
                >
                    {!newPortalCode ? (
                        <>
                            <div className="flex items-start gap-3 rounded-lg bg-light-orange/40 border border-dark-orange/20 px-4 py-3">
                                <WarningAmberIcon fontSize="small" className="text-dark-orange mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-darker-orange leading-relaxed">
                                    The existing portal code will be <strong>immediately invalidated</strong>. The partner will not be able to use it to start a new claim.
                                </p>
                            </div>
                            <ul className="flex flex-col gap-2 text-sm text-grey">
                                <li className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-light-grey flex-shrink-0" />
                                    A new code will be generated using the same length and format as the original
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-light-grey flex-shrink-0" />
                                    You must share the new code with the partner, it will only be shown once
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-light-grey flex-shrink-0" />
                                    Existing credentials and pending claim emails are not affected
                                </li>
                            </ul>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-grey">The new portal code has been generated. Share it with the partner, it will not be shown again.</p>
                            <div className="flex items-center gap-3 rounded-lg bg-glacier-grey border border-smokey-purple/20 px-4 py-3">
                                <span className="flex-1 font-mono text-lg font-semibold tracking-widest text-dark-dark-grey select-all">{newPortalCode}</span>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(newPortalCode);
                                        setCopied(true);
                                        setTimeout(() => setCopied(false), 2000);
                                    }}
                                    className={`text-xs transition-colors ${copied ? "text-titanium-violet" : "text-grey hover:text-dark-dark-grey"}`}
                                >
                                    {copied ? "Copied!" : "Copy"}
                                </button>
                            </div>
                        </>
                    )}
                </Dialog>
            )}

            {isDev && showDevRolloverModal && (
                <Dialog
                    title="Create rollover credential"
                    confirmLabel="Create"
                    variant="warning"
                    error={devRolloverError}
                    loading={devRolloverLoading}
                    onConfirm={handleDevRollover}
                    onClose={() => { if (!devRolloverLoading) setShowDevRolloverModal(false); }}
                >
                    <WarningBox>
                        Creates a new credential backdated past the rollover threshold so it is immediately due to roll. For development use only.
                    </WarningBox>
                </Dialog>
            )}

            {isDev && showDevResetModal && (
                <Dialog
                    title="Dev Reset"
                    confirmLabel="Delete all"
                    variant="warning"
                    error={devResetError}
                    loading={devResetLoading}
                    onConfirm={handleDevReset}
                    onClose={() => { if (!devResetLoading) setShowDevResetModal(false); }}
                >
                    <WarningBox>
                        This will permanently delete all credentials and claim history for this app. For development use only.
                    </WarningBox>
                </Dialog>
            )}
        </div>
    );
}

export default AppCredentialsPage;
